import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BASE_URL } from '../context/api'; // Adjust the path according to your project structure

const Loader = () => (
  <div className="flex items-center justify-center">
    <div className="relative">
      <div className="w-12 h-12 rounded-full absolute border-4 border-pink-200"></div>
      <div className="w-12 h-12 rounded-full animate-spin absolute border-4 border-pink-600 border-t-transparent"></div>
    </div>
  </div>
);

const RegistrationFees = () => {
  const [fees, setFees] = useState([]);
  const [form1to4Fee, setForm1to4Fee] = useState('');
  const [form5to6Fee, setForm5to6Fee] = useState('');
  const [loading, setLoading] = useState(true);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    fetchRegistrationFees();
  }, []);

  // Fetch the current registration fees from the backend
  const fetchRegistrationFees = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/registrations`);
      const data = response.data.data;

      // Find the fees for the two ranges
      const form1to4 = data.find((fee) => fee.form_range === 'Form 1-4');
      const form5to6 = data.find((fee) => fee.form_range === 'Form 5-6');

      setFees(data);
      setForm1to4Fee(form1to4 ? form1to4.amount : '');
      setForm5to6Fee(form5to6 ? form5to6.amount : '');
      setLoading(false);
    } catch (error) {
      console.error('Error fetching registration fees:', error);
      setErrorMessage('Failed to fetch registration fees.');
      setLoading(false);
    }
  };

  // Handle the update for Form 1-4
  const handleUpdateForm1to4Fee = async () => {
    if (!form1to4Fee || isNaN(form1to4Fee)) {
      setErrorMessage('Please enter a valid fee for Form 1-4');
      return;
    }
    try {
      await axios.put(`${BASE_URL}/registrations`, {
        formRange: 'Form 1-4',
        newAmount: form1to4Fee
      });
      setSuccessMessage('Form 1-4 fee updated successfully.');
      setErrorMessage('');
      fetchRegistrationFees();
    } catch (error) {
      console.error('Error updating Form 1-4 fee:', error);
      setErrorMessage('Failed to update Form 1-4 fee.');
    }
  };

  // Handle the update for Form 5-6
  const handleUpdateForm5to6Fee = async () => {
    if (!form5to6Fee || isNaN(form5to6Fee)) {
      setErrorMessage('Please enter a valid fee for Form 5-6');
      return;
    }
    try {
      await axios.put(`${BASE_URL}/registrations`, {
        formRange: 'Form 5-6',
        newAmount: form5to6Fee
      });
      setSuccessMessage('Form 5-6 fee updated successfully.');
      setErrorMessage('');
      fetchRegistrationFees();
    } catch (error) {
      console.error('Error updating Form 5-6 fee:', error);
      setErrorMessage('Failed to update Form 5-6 fee.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-800">Registration Fees</h1>
            <div className="h-1 w-full bg-gray-200 mt-2"></div>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-12">
              <Loader />
            </div>
          ) : (
            <div>
              <div className="overflow-x-auto rounded-lg border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-pink-600 rounded-t-lg">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider rounded-tl-lg">Form Range</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Current Fee (USD)</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">New Fee (USD)</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider rounded-tr-lg">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {/* Form 1-4 */}
                    <tr className="hover:bg-pink-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Form 1-4</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {fees.find(fee => fee.form_range === 'Form 1-4')?.amount}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <input
                          type="text"
                          value={form1to4Fee}
                          onChange={(e) => setForm1to4Fee(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                          placeholder="Enter new fee"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={handleUpdateForm1to4Fee}
                          className="text-pink-600 hover:text-pink-900 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2"
                        >
                          Update
                        </button>
                      </td>
                    </tr>

                    {/* Form 5-6 */}
                    <tr className="hover:bg-pink-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Form 5-6</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {fees.find(fee => fee.form_range === 'Form 5-6')?.amount}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <input
                          type="text"
                          value={form5to6Fee}
                          onChange={(e) => setForm5to6Fee(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                          placeholder="Enter new fee"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={handleUpdateForm5to6Fee}
                          className="text-pink-600 hover:text-pink-900 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2"
                        >
                          Update
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Success or Error Message */}
              {successMessage && (
                <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-md">
                  <p className="text-green-700">{successMessage}</p>
                </div>
              )}
              {errorMessage && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-red-700">{errorMessage}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RegistrationFees;
